import type * as Y from "yjs";
import { newId } from "./blocks";

export const ASKS_KEY = "asks";

export const MAX_ASK_OPTIONS = 4;
export const MAX_ASK_QUESTIONS = 6;

export type AskOption = { label: string; description?: string };

export type AskQuestion = {
  question: string;
  header?: string;
  options: AskOption[];
  multiSelect: boolean;
  allowOther?: boolean;
};

export type AskAudience = { kind: "board" } | { kind: "user"; userId: string };

export type AskAnswer = { selected: string[]; other: string };

export type AskPerson = { userId: string; userName: string };

/** One shared draft per question set — everybody edits the same selection. */
export type AskDraft = {
  step: number;
  answers: AskAnswer[];
  updatedBy: string;
  updatedByName: string;
  updatedAt: number;
  contributors: AskPerson[];
};

export type AskResult = {
  answers: AskAnswer[];
  byUserId: string;
  byUserName: string;
  at: number;
  contributors: AskPerson[];
};

export type Ask = {
  id: string;
  byUserId: string;
  byUserName: string;
  audience: AskAudience;
  questions: AskQuestion[];
  draft: AskDraft;
  result: AskResult | null;
  at: number;
  status: "open" | "closed";
};

export function asksMap(doc: Y.Doc): Y.Map<Ask> {
  return doc.getMap<Ask>(ASKS_KEY);
}

export function createAsk(
  doc: Y.Doc,
  input: {
    userId: string;
    userName: string;
    audience: AskAudience;
    questions: AskQuestion[];
    at?: number;
  },
): Ask {
  const questions = input.questions.slice(0, MAX_ASK_QUESTIONS).map((question) => ({
    ...question,
    options: question.options.slice(0, MAX_ASK_OPTIONS),
    allowOther: question.allowOther ?? true,
  }));

  const ask: Ask = {
    id: newId("ak"),
    byUserId: input.userId,
    byUserName: input.userName,
    audience: input.audience,
    questions,
    draft: {
      step: 0,
      answers: questions.map(() => ({ selected: [], other: "" })),
      updatedBy: "",
      updatedByName: "",
      updatedAt: input.at ?? Date.now(),
      contributors: [],
    },
    result: null,
    at: input.at ?? Date.now(),
    status: "open",
  };
  asksMap(doc).set(ask.id, ask);
  return ask;
}

export function readAsk(doc: Y.Doc, askId: string): Ask | null {
  return asksMap(doc).get(askId) ?? null;
}

export function readAsks(doc: Y.Doc): Ask[] {
  return [...asksMap(doc).values()];
}

export function askDraft(doc: Y.Doc, askId: string): AskDraft | null {
  return readAsk(doc, askId)?.draft ?? null;
}

export function askResult(doc: Y.Doc, askId: string): AskResult | null {
  return readAsk(doc, askId)?.result ?? null;
}

export function askIsAnswered(doc: Y.Doc, askId: string): boolean {
  const ask = readAsk(doc, askId);
  return !ask || ask.result !== null || ask.status === "closed";
}

function addresses(ask: Ask, userId: string): boolean {
  return ask.audience.kind === "board" || ask.audience.userId === userId;
}

/** The oldest unanswered question this person is part of. */
export function openAskFor(doc: Y.Doc, userId: string): Ask | null {
  return (
    readAsks(doc)
      .filter((ask) => ask.status === "open" && ask.result === null && addresses(ask, userId))
      .sort((a, b) => a.at - b.at)[0] ?? null
  );
}

function withContributor(contributors: AskPerson[], person: AskPerson): AskPerson[] {
  return contributors.some((entry) => entry.userId === person.userId)
    ? contributors
    : [...contributors, person];
}

function updateDraft(
  doc: Y.Doc,
  askId: string,
  person: AskPerson,
  change: (draft: AskDraft) => AskDraft,
  at: number,
): void {
  const ask = readAsk(doc, askId);
  if (!ask || ask.status !== "open" || ask.result) return;

  asksMap(doc).set(askId, {
    ...ask,
    draft: {
      ...change(ask.draft),
      updatedBy: person.userId,
      updatedByName: person.userName,
      updatedAt: at,
      contributors: withContributor(ask.draft.contributors, person),
    },
  });
}

/** Everybody edits the same answer, so one person's click is visible to all. */
export function setAskDraft(
  doc: Y.Doc,
  askId: string,
  questionIndex: number,
  patch: Partial<AskAnswer>,
  person: AskPerson,
  at = Date.now(),
): void {
  updateDraft(
    doc,
    askId,
    person,
    (draft) => ({
      ...draft,
      answers: draft.answers.map((answer, index) =>
        index === questionIndex ? { ...answer, ...patch } : answer,
      ),
    }),
    at,
  );
}

/** Moving on happens together — nobody is left on another question. */
export function setAskStep(
  doc: Y.Doc,
  askId: string,
  step: number,
  person: AskPerson,
  at = Date.now(),
): void {
  const ask = readAsk(doc, askId);
  if (!ask) return;
  const bounded = Math.max(0, Math.min(step, ask.questions.length - 1));
  updateDraft(doc, askId, person, (draft) => ({ ...draft, step: bounded }), at);
}

export function submitAsk(doc: Y.Doc, askId: string, person: AskPerson, at = Date.now()): void {
  const ask = readAsk(doc, askId);
  // First send wins; a second one must not overwrite the shared answer.
  if (!ask || ask.status !== "open" || ask.result) return;

  asksMap(doc).set(askId, {
    ...ask,
    result: {
      answers: ask.draft.answers,
      byUserId: person.userId,
      byUserName: person.userName,
      at,
      contributors: withContributor(ask.draft.contributors, person),
    },
  });
}

export function closeAsk(doc: Y.Doc, askId: string): void {
  const ask = readAsk(doc, askId);
  if (ask) asksMap(doc).set(askId, { ...ask, status: "closed" });
}

export function pruneClosedAsks(doc: Y.Doc, olderThan: number): void {
  doc.transact(() => {
    for (const ask of readAsks(doc)) {
      if ((ask.status === "closed" || ask.result) && ask.at < olderThan) asksMap(doc).delete(ask.id);
    }
  });
}
